import { Metrics } from './chatMetrics';
import { isArborHost } from './isArborHost';
import { reportError, reportWarning } from './reportError';

// Single-responsibility metrics sink for the chat client. Kept separate from
// ChatLogger (which is debug/recording logging only): transports and the
// coordinator call these methods for domain events and this maps them to
// metrics. Per-instance -- it holds timer-correlation state, so each client
// MUST get its own instance (chatManager runs concurrent clients per
// conversation and shared timer state would cross-contaminate).
/**
 * What a mid-turn silence window turned out to be, once the transport asked the
 * store whether the turn was still running.
 *
 * - `turn-running`  -- the backend is still working; the stream simply had
 *                      nothing to say (a long tool call, a slow first token).
 *                      Expected and harmless; the turn was NOT interrupted.
 * - `recovered`     -- the turn had already finished; its closing events were
 *                      lost in transit and were reconciled from the store.
 * - `turn-failed`   -- the turn genuinely failed server-side.
 * - `unverifiable`  -- the turn's state could not be read at all.
 * - `exhausted`     -- the turn kept reporting as running past the check cap.
 *
 * Only the last three surface an error to the user.
 */
/**
 * A finer-grained cause for a `TRANSPORT_ERROR`. On its own `TRANSPORT_ERROR`
 * is a catch-all -- a dropped Ably connection, an unparseable event and a
 * stalled stream all land in it -- so alerting on it cannot separate an
 * actionable failure from a transient one. `chirp-rpc` is used for every other
 * error type so the tag is always present and meaningful.
 */
const TRANSPORT_ERROR_MATCHERS = [{
  reason: 'ably-suspended',
  matches: message => message.startsWith('Ably connection suspended')
}, {
  reason: 'ably-channel-failed',
  matches: message => message.startsWith('Ably channel recovery failed') || message.startsWith('Ably channel entered failed state')
}, {
  reason: 'ably-parse-error',
  matches: message => message.startsWith('Failed to parse Ably message')
}, {
  reason: 'ably-oversized-event',
  matches: message => message.includes('oversized Ably event')
}, {
  reason: 'send-failed',
  matches: message => message.includes('Transport send failed')
}, {
  reason: 'stall-unverifiable',
  matches: message => message.includes('no events for 30s') && message.includes('could not be verified')
}, {
  reason: 'stall-exhausted',
  matches: message => message.includes('still reported as running')
}];
export function classifyTransportError(error) {
  var _matched$reason;
  if (error.type !== 'TRANSPORT_ERROR') return 'unknown';
  const matched = TRANSPORT_ERROR_MATCHERS.find(({
    matches
  }) => matches(error.message));
  return (_matched$reason = matched === null || matched === void 0 ? void 0 : matched.reason) !== null && _matched$reason !== void 0 ? _matched$reason : 'unknown';
}

/**
 * Fine-grained category for CHIRP RPC errors (anything with
 * `error_source: 'chirp-rpc'`). Used as the `error_category` metric tag.
 *
 * Typed errors (`INTERNAL_ERROR`, `NOT_FOUND`, and the entries in
 * `TYPED_CHIRP_ERROR_CATEGORIES`) map directly from their error type.
 * `UNKNOWN`-typed errors are classified by message pattern matching; unmatched
 * patterns fall to `'unknown'` to keep the label set bounded. The raw
 * `error.message` is preserved in the Sentry `extra` payload as `chirp_message`.
 */

const CHIRP_ERROR_MATCHERS = [{
  category: 'access-control',
  matches: message => message.includes('AI access control')
}, {
  category: 'context-too-large',
  matches: message => message.includes('context too large')
}, {
  category: 'request-timeout',
  matches: message => message.includes('Request timeout')
}, {
  category: 'not-found',
  matches: message => message === 'Not found'
}, {
  category: 'network-failure',
  matches: message => message.includes('Network request failed')
}, {
  category: 'request-aborted',
  matches: message => message.includes('Request aborted')
}];
const TYPED_CHIRP_ERROR_CATEGORIES = {
  ACCESS_DENIED: 'access-denied',
  AGENT_NOT_FOUND: 'agent-not-found',
  AGENT_DELETED: 'agent-deleted',
  UNAUTHORIZED: 'unauthorized',
  INVALID_REQUEST: 'invalid-request',
  ACCESS_CONTROL_DISABLED: 'access-control-disabled',
  MISSING_REQUIRED_SCOPE: 'missing-required-scope',
  AI_SETTINGS_NOT_ENABLED: 'ai-settings-not-enabled',
  FILE_NOT_FOUND: 'file-not-found',
  FILE_TOO_LARGE: 'file-too-large',
  TOTAL_UPLOAD_TOO_LARGE: 'total-upload-too-large',
  INVALID_PATH: 'invalid-path',
  AGENT_KILLED: 'agent-killed',
  INVALID_ARTIFACT_STATUS: 'invalid-artifact-status',
  CALLER_CONTEXT_TOO_LARGE: 'caller-context-too-large',
  TURN_NOT_CANCELLABLE: 'turn-not-cancellable',
  TOOL_ERROR: 'tool-error',
  AUTH_FAILURE_PERMANENT: 'auth-failure-permanent'
};
export function classifyChirpError(error) {
  var _matched$category;
  if (error.type === 'INTERNAL_ERROR') return 'internal';
  if (error.type === 'NOT_FOUND') return 'not-found';
  if (error.type !== 'UNKNOWN') {
    var _TYPED_CHIRP_ERROR_CA;
    return typeof error.type === 'string' ? (_TYPED_CHIRP_ERROR_CA = TYPED_CHIRP_ERROR_CATEGORIES[error.type]) !== null && _TYPED_CHIRP_ERROR_CA !== void 0 ? _TYPED_CHIRP_ERROR_CA : 'unknown' : 'unknown';
  }
  const matched = CHIRP_ERROR_MATCHERS.find(({
    matches
  }) => matches(error.message));
  return (_matched$category = matched === null || matched === void 0 ? void 0 : matched.category) !== null && _matched$category !== void 0 ? _matched$category : 'unknown';
}
function classifyError(error) {
  if (error.type === 'TRANSPORT_ERROR') {
    return {
      errorSource: 'transport',
      errorCategory: classifyTransportError(error)
    };
  }
  return {
    errorSource: 'chirp-rpc',
    errorCategory: classifyChirpError(error)
  };
}
const NOOP = () => {};
export const noopTelemetry = {
  turnStarted: NOOP,
  turnCompleted: NOOP,
  turnCancelled: NOOP,
  turnError: NOOP,
  turnRecoveryAttempt: NOOP,
  turnRecovered: NOOP,
  turnRecoveryExhausted: NOOP,
  tokenReceived: NOOP,
  toolExecutionStarted: NOOP,
  toolExecutionFinished: NOOP,
  streamError: NOOP,
  streamSilence: NOOP,
  streamStalled: NOOP,
  resumeRejectionSuppressed: NOOP,
  subagentError: NOOP,
  clientPauseRestored: NOOP,
  steerAttempted: NOOP,
  turnSteered: NOOP,
  steerDropped: NOOP
};
export function createChatTelemetry(mode) {
  if (isArborHost()) return noopTelemetry;
  const toolStartTimes = new Map();
  let turnStartedAt = null;
  let hasReceivedFirstToken = false;
  let turnToolCallCount = 0;
  function resetTurnState() {
    turnStartedAt = null;
    hasReceivedFirstToken = false;
    turnToolCallCount = 0;
  }
  return {
    turnStarted() {
      turnStartedAt = performance.now();
      hasReceivedFirstToken = false;
      turnToolCallCount = 0;
    },
    turnCompleted() {
      Metrics.counter('turn-completed', {
        mode
      }).increment();
      if (turnStartedAt !== null) {
        Metrics.timer('turn-duration', {
          mode
        }).update(performance.now() - turnStartedAt);
      }
      Metrics.histogram('tools-per-turn', {
        mode
      }).update(turnToolCallCount);
      resetTurnState();
    },
    turnCancelled() {
      Metrics.counter('turn-cancelled', {
        mode
      }).increment();
    },
    turnError(error) {
      const {
        errorSource,
        errorCategory
      } = classifyError(error);
      Metrics.counter('turn-error', {
        error_source: errorSource,
        error_category: errorCategory,
        mode
      }).increment();
      const reportable = error.cause instanceof Error ? error.cause : new Error(error.message);
      reportWarning(reportable.message, {
        scope: 'turn-error',
        extra: {
          mode,
          error_source: errorSource,
          error_category: errorCategory,
          chirp_message: error.message,
          stack: reportable.stack
        },
        fingerprint: [errorSource, errorCategory]
      });
      resetTurnState();
    },
    // Same error_source/error_category/mode tag shape as turn-error so the
    // recovery funnel (attempt -> recovered | exhausted) can be broken down and
    // compared against the surfaced turn-error rate in one query.
    turnRecoveryAttempt(error) {
      const {
        errorSource,
        errorCategory
      } = classifyError(error);
      Metrics.counter('turn-recovery-attempt', {
        error_source: errorSource,
        error_category: errorCategory,
        mode
      }).increment();
    },
    turnRecovered(error) {
      const {
        errorSource,
        errorCategory
      } = classifyError(error);
      Metrics.counter('turn-recovered', {
        error_source: errorSource,
        error_category: errorCategory,
        mode
      }).increment();
    },
    turnRecoveryExhausted(error) {
      const {
        errorSource,
        errorCategory
      } = classifyError(error);
      Metrics.counter('turn-recovery-exhausted', {
        error_source: errorSource,
        error_category: errorCategory,
        mode
      }).increment();
    },
    tokenReceived() {
      if (hasReceivedFirstToken || turnStartedAt === null) return;
      hasReceivedFirstToken = true;
      Metrics.timer('first-token-latency', {
        mode
      }).update(performance.now() - turnStartedAt);
    },
    toolExecutionStarted(toolUseId) {
      turnToolCallCount++;
      toolStartTimes.set(toolUseId, performance.now());
    },
    toolExecutionFinished(toolUseId, toolName, status) {
      const startTime = toolStartTimes.get(toolUseId);
      if (startTime === undefined) return;
      toolStartTimes.delete(toolUseId);
      const elapsed = performance.now() - startTime;
      Metrics.timer('tool-execution-duration', {
        tool_name: toolName,
        status,
        mode
      }).update(elapsed);
    },
    streamError(error) {
      const {
        errorSource,
        errorCategory
      } = classifyError(error);
      Metrics.counter('stream-error', {
        error_source: errorSource,
        error_category: errorCategory,
        mode
      }).increment();
      const reportable = error.cause instanceof Error ? error.cause : new Error(error.message);
      reportWarning(reportable.message, {
        scope: 'stream-error',
        extra: {
          mode,
          error_source: errorSource,
          error_category: errorCategory,
          chirp_message: error.message,
          stack: reportable.stack
        },
        fingerprint: [errorSource, errorCategory]
      });
    },
    streamSilence(resolution) {
      Metrics.counter('stream-silence-total', {
        resolution,
        mode
      }).increment();
    },
    streamStalled() {
      Metrics.counter('stream-stalled-total', {
        mode
      }).increment();
      reportError(new Error('Stream stalled: no events received within the timeout period'), {
        scope: 'stream-stall',
        extra: {
          mode
        },
        fingerprint: ['stream-stall']
      });
    },
    resumeRejectionSuppressed() {
      Metrics.counter('resume-rejection-suppressed', {
        mode
      }).increment();
    },
    subagentError(message) {
      Metrics.counter('subagent-error', {
        mode
      }).increment();
      reportWarning('Subagent-scoped error frame (parent turn continued)', {
        scope: 'subagent-error',
        extra: {
          mode,
          message
        }
      });
    },
    clientPauseRestored({
      source,
      kind
    }) {
      Metrics.counter('client-pause-restored', {
        source,
        kind,
        mode
      }).increment();
    },
    steerAttempted() {
      Metrics.counter('steer-attempted-total', {
        mode
      }).increment();
    },
    turnSteered(disposition) {
      Metrics.counter('turn-steered-total', {
        disposition: String(disposition),
        mode
      }).increment();
    },
    steerDropped({
      reason,
      status
    }) {
      Metrics.counter('steer-dropped-total', {
        reason,
        status,
        mode
      }).increment();
    }
  };
}